import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiDownload, FiEye, FiTrash2, FiMail, FiPhone, FiBriefcase, FiCalendar, FiCheck, FiX, FiUser, FiMapPin, FiFilter } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const Applications = () => {
  const toast = UseToast();
  const [searchParams] = useSearchParams();
  const jobIdFilter = searchParams.get('jobId');
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [jobIdFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/applications`;
      if (jobIdFilter) url += `?jobId=${jobIdFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setApplications(data.data || []);
      } else {
        toast.error('Failed to load applications');
        setApplications([]);
      }
    } catch (error) {
      toast.error('Server error');
      console.error('Fetch error:', error);
      setApplications([]);
    }
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/applications/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Status updated to ${newStatus}! Mail sent to candidate.`);
        setApplications(applications.map(app => 
          app._id === id? {...app, status: newStatus } : app
        ));
        if (selectedApp && selectedApp._id === id) {
          setSelectedApp({...selectedApp, status: newStatus });
        }
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Status update error:', error);
    }
    setUpdatingStatus(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/applications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Application deleted');
        setApplications(applications.filter(app => app._id!== id));
        setShowModal(false);
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Delete error:', error);
    }
  };

  const handleView = (app) => {
    setSelectedApp(app);
    setShowModal(true);
    if (app.status === 'new') {
      handleStatusChange(app._id, 'reviewing');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { color: '#F59E0B', bg: '#FEF3C7', label: 'New' },
      reviewing: { color: '#3B82F6', bg: '#DBEAFE', label: 'Reviewing' },
      shortlisted: { color: '#8B5CF6', bg: '#EDE9FE', label: 'Shortlisted' },
      rejected: { color: '#EF4444', bg: '#FEE2E2', label: 'Rejected' },
      hired: { color: '#10B981', bg: '#D1FAE5', label: 'Hired' }
    };
    return statusMap[status] || statusMap.new;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredApps = applications.filter(app => {
    const matchesSearch = 
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.jobId?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <>
      <style>{`
.apps-page { font-family: 'Outfit', sans-serif; }
.page-header { margin-bottom: 30px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 20px; }
.page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0; }
.page-subtitle { color: #64748B; font-size: 15px; margin: 0; }
.filters-bar { background: #FFF; padding: 20px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin-bottom: 24px; display: flex; gap: 16px; align-items: center; }
.search-input { flex: 1; display: flex; align-items: center; gap: 10px; background: #F1F5F9; padding: 12px 16px; border-radius: 10px; }
.search-input input { border: none; background: none; outline: none; flex: 1; font-size: 14px; font-family: inherit; }
.filter-select { padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; background: #FFF; font-family: inherit; }
.apps-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(380px, 1fr)); gap: 24px; }
.app-card { background: #FFF; border-radius: 16px; padding: 24px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55); cursor: pointer; border: 2px solid transparent; }
.app-card:hover { transform: translateY(-8px); box-shadow: 0 12px 40px rgba(59, 130, 246, 0.15); border-color: #3B82F6; }
.app-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; }
.app-avatar { width: 60px; height: 60px; border-radius: 12px; background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); display: flex; align-items: center; justify-content: center; color: #FFF; font-size: 24px; font-weight: 700; }
.app-info h3 { font-size: 18px; font-weight: 700; color: #0F172A; margin: 0 0 4px 0; }
.app-info p { font-size: 14px; color: #64748B; margin: 0; }
.app-details { display: flex; flex-direction: column; gap: 10px; margin: 16px 0; padding: 16px 0; border-top: 1px solid #F1F5F9; border-bottom: 1px solid #F1F5F9; }
.detail-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #475569; }
.detail-item svg { color: #3B82F6; font-size: 16px; flex-shrink: 0; }
.app-actions { display: flex; gap: 10px; }
.btn { flex: 1; padding: 10px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Outfit', sans-serif; }
.btn-view { background: #F1F5F9; color: #475569; }
.btn-view:hover { background: #E2E8F0; }
.btn-download { background: #DBEAFE; color: #3B82F6; }
.btn-download:hover { background: #3B82F6; color: #FFF; }
.loading-state,.empty-state { text-align: center; padding: 60px 20px; color: #64748B; }
.empty-icon { font-size: 64px; margin-bottom: 16px; opacity: 0.5; }
.modal-body { padding: 24px; }
.modal-section { margin-bottom: 24px; }
.modal-section h4 { font-size: 16px; font-weight: 700; color: #0F172A; margin: 0 0 12px 0; }
.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.info-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #475569; }
.info-item svg { color: #3B82F6; font-size: 18px; flex-shrink: 0; }
.modal-actions { display: flex; gap: 12px; padding: 24px; border-top: 1px solid #E2E8F0; flex-wrap: wrap; }
.modal-btn { padding: 12px 20px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Outfit', sans-serif; }
.modal-btn-submit { background: linear-gradient(135deg, #10B981 0%, #059669 100%); color: #FFF; }
.modal-btn-submit:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3); }
.modal-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.modal-btn-reject { background: #FEE2E2; color: #EF4444; }
.modal-btn-reject:hover { background: #EF4444; color: #FFF; }
.modal-btn-shortlist { background: #EDE9FE; color: #8B5CF6; }
.modal-btn-shortlist:hover { background: #8B5CF6; color: #FFF; }
.modal-btn-delete { background: #FEE2E2; color: #EF4444; }
.modal-btn-delete:hover { background: #EF4444; color: #FFF; }
.status-dropdown { padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; background: #FFF; font-family: inherit; width: 100%; }
        @media (max-width: 768px) {
   .apps-grid { grid-template-columns: 1fr; }
   .info-grid { grid-template-columns: 1fr; }
   .modal-actions { flex-direction: column; }
        }
      `}</style>
      
      <div className="apps-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Job Applications</h1>
            <p className="page-subtitle">Manage and review candidate applications</p>
          </div>
          <Badge color="#3B82F6" bg="#DBEAFE">
            {applications.filter(a => a.status === 'new').length} New
          </Badge>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <FiFilter />
            <input 
              type="text" 
              placeholder="Search by name, email, job..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="rejected">Rejected</option>
            <option value="hired">Hired</option>
          </select>
        </div>

        {loading? (
          <div className="loading-state">Loading applications...</div>
        ) : filteredApps.length === 0? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A' }}>No applications found</p>
            <p style={{ fontSize: '14px', color: '#64748B' }}>Applications will appear here when submitted</p>
          </div>
        ) : (
          <div className="apps-grid">
            {filteredApps.map(app => {
              const badge = getStatusBadge(app.status);
              return (
                <div key={app._id} className="app-card" onClick={() => handleView(app)}>
                  <div className="app-header">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div className="app-avatar">{app.name?.[0]?.toUpperCase()}</div>
                      <div className="app-info">
                        <h3>{app.name}</h3>
                        <p>{app.jobId?.title || 'Unknown Job'}</p>
                      </div>
                    </div>
                    <Badge color={badge.color} bg={badge.bg}>{badge.label}</Badge>
                  </div>

                  <div className="app-details">
                    <div className="detail-item">
                      <FiMail /> {app.email}
                    </div>
                    <div className="detail-item">
                      <FiPhone /> {app.phone}
                    </div>
                    <div className="detail-item">
                      <FiBriefcase /> {app.jobId?.location || 'N/A'}
                    </div>
                    <div className="detail-item">
                      <FiCalendar /> {formatDate(app.createdAt)}
                    </div>
                  </div>

                  <div className="app-actions">
                    <button className="btn btn-view" onClick={(e) => { e.stopPropagation(); handleView(app); }}>
                      <FiEye /> View
                    </button>
                    <a 
                      href={app.resume} 
                      download 
                      className="btn btn-download" 
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FiDownload /> Resume
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Modal 
          isOpen={showModal} 
          onClose={() => setShowModal(false)} 
          title="Application Details"
          size="md"
        >
          {selectedApp && (
            <>
              <div className="modal-body">
                <div className="modal-section">
                  <h4>Candidate Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <FiUser /> <span>{selectedApp.name}</span>
                    </div>
                    <div className="info-item">
                      <FiMail /> <span>{selectedApp.email}</span>
                    </div>
                    <div className="info-item">
                      <FiPhone /> <span>{selectedApp.phone}</span>
                    </div>
                    <div className="info-item">
                      <FiMapPin /> <span>{selectedApp.location || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="modal-section">
                  <h4>Job Applied</h4>
                  <p><strong>Position:</strong> {selectedApp.jobId?.title || 'N/A'}</p>
                  <p><strong>Location:</strong> {selectedApp.jobId?.location || 'N/A'}</p>
                  <p><strong>Applied:</strong> {formatDate(selectedApp.createdAt)}</p>
                </div>

                {selectedApp.subject && (
                  <div className="modal-section">
                    <h4>Subject</h4>
                    <p>{selectedApp.subject}</p>
                  </div>
                )}

                {selectedApp.message && (
                  <div className="modal-section">
                    <h4>Cover Letter</h4>
                    <p style={{ background: '#F8FAFC', padding: '16px', borderRadius: '10px', whiteSpace: 'pre-wrap' }}>
                      {selectedApp.message}
                    </p>
                  </div>
                )}

                <div className="modal-section">
                  <h4>Resume</h4>
                  <a 
                    href={selectedApp.resume} 
                    download 
                    className="btn btn-download" 
                    style={{ width: 'auto', display: 'inline-flex' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiDownload /> Download Resume
                  </a>
                </div>

                <div className="modal-section">
                  <h4>Update Status</h4>
                  <select 
                    className="status-dropdown"
                    value={selectedApp.status}
                    onChange={(e) => handleStatusChange(selectedApp._id, e.target.value)}
                    disabled={updatingStatus}
                  >
                    <option value="new">New</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="rejected">Rejected</option>
                    <option value="hired">Hired</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="modal-btn modal-btn-submit"
                  onClick={() => handleStatusChange(selectedApp._id, 'shortlisted')}
                  disabled={updatingStatus || selectedApp.status === 'shortlisted'}
                >
                  <FiCheck /> Submit & Shortlist
                </button>
                <button 
                  className="modal-btn modal-btn-shortlist"
                  onClick={() => handleStatusChange(selectedApp._id, 'hired')}
                  disabled={updatingStatus || selectedApp.status === 'hired'}
                >
                  <FiCheck /> Submit & Hire
                </button>
                <button 
                  className="modal-btn modal-btn-reject"
                  onClick={() => handleStatusChange(selectedApp._id, 'rejected')}
                  disabled={updatingStatus || selectedApp.status === 'rejected'}
                >
                  <FiX /> Submit & Reject
                </button>
                <button 
                  className="modal-btn modal-btn-delete"
                  onClick={() => handleDelete(selectedApp._id)}
                  disabled={updatingStatus}
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            </>
          )}
        </Modal>
      </div>
    </>
  );
};

export default Applications;